import React, { useEffect, useState } from 'react';

import { Button, Card, Form, Select, Space, TimePicker } from 'antd';

import dayjs from 'dayjs';

const minutesToHHmm = minutes => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return dayjs().hour(hrs).minute(mins).second(0);
};

const RuleTargetScheduler = ({ rule, targetStep, onTargetsChange, isNew }) => {
  const [targets, setTargets] = useState([]);

  useEffect(() => {
    onTargetsChange(targets);
  }, [targets]);

  useEffect(() => {
    if (!rule?.timeBased) return;

    if (isNew || !rule.targets?.length) {
      const startTime = rule?.data?.variables?.start_time;
      const endTime = rule?.data?.variables?.end_time;
      const scheduleVariance = rule?.data?.variables?.schedule_variance;

      setTargets(prevTargets => {
        const firstTarget = {
          target: rule.data.target?.value,
          startTime: startTime ? dayjs(startTime, 'HH:mm') : null,
          endTime: startTime ? dayjs(endTime, 'HH:mm') : null,
          scheduleVariance: scheduleVariance ? dayjs(scheduleVariance, 'HH:mm') : null,
          locked: true
        };

        const rest = prevTargets?.slice(1) || [];

        return [firstTarget, ...rest];
      });
    } else if (!isNew) {
      const targetsToSet = rule.targets.map(({ geofence_id, start_time, end_time, schedule_variance }, idx) => ({
        target: geofence_id,
        startTime: dayjs(start_time, 'HH:mm'),
        endTime: dayjs(end_time, 'HH:mm'),
        scheduleVariance: minutesToHHmm(schedule_variance),
        locked: !idx
      }));

      setTargets(targetsToSet);
    }
  }, [rule]);

  const handleChange = (index, field, value) => {
    const updated = [...targets];
    updated[index][field] = value;
    updated[index].error = checkOverlap(updated, index);

    setTargets(updated);
  };

  const addTarget = () => {
    setTargets([
      ...targets,
      { target: undefined, startTime: undefined, endTime: undefined, scheduleVariance: undefined }
    ]);
  };

  const removeTarget = index => {
    const updated = [...targets];
    updated.splice(index, 1);
    setTargets(updated);
  };

  const isBlockComplete = block => {
    return !!(block.target && block.startTime && block.endTime && block.scheduleVariance && !block.error);
  };

  const checkOverlap = (blocks, currentIndex) => {
    const current = blocks[currentIndex];
    if (!current.startTime || !current.endTime || !current.scheduleVariance) return;

    const variance = current.scheduleVariance.hour() * 60 + current.scheduleVariance.minute();
    const currentStart = current.startTime.subtract(variance, 'minute');
    const currentEnd = current.endTime.add(variance, 'minute');

    for (let i = 0; i < blocks.length; i++) {
      if (i === currentIndex) continue;
      const b = blocks[i];
      if (!b.startTime || !b.endTime || !b.scheduleVariance) continue;

      const v = b.scheduleVariance.hour() * 60 + b.scheduleVariance.minute();
      const bStart = b.startTime.subtract(v, 'minute');
      const bEnd = b.endTime.add(v, 'minute');

      if (currentStart.isBefore(bEnd) && currentEnd.isAfter(bStart)) {
        return `Time overlaps with Target #${i + 1}`;
      }
    }
    return;
  };

  if (!rule.timeBased) {
    return null;
  }

  return (
    <div>
      {targets.map((block, index) => {
        return (
          <Card
            key={index}
            title={`Target #${index + 1}`}
            extra={
              !block.locked && (
                <Button danger type="link" onClick={() => removeTarget(index)}>
                  Delete
                </Button>
              )
            }
            className="mb-4">
            <Form layout="vertical">
              <Form.Item label="Target" required>
                <Select
                  disabled={block.locked}
                  placeholder="Select"
                  value={block.target}
                  options={targetStep?.options || []}
                  showSearch
                  onChange={val => handleChange(index, 'target', val)}
                />
              </Form.Item>

              <Space size="large" wrap>
                <Form.Item label="Start time" required validateStatus={block.error ? 'error' : ''} help={block.error}>
                  <TimePicker
                    disabled={block.locked}
                    format="HH:mm"
                    value={block.startTime || null}
                    onChange={val => handleChange(index, 'startTime', val)}
                  />
                </Form.Item>

                <Form.Item label="End time" required validateStatus={block.error ? 'error' : ''} help={block.error}>
                  <TimePicker
                    disabled={block.locked}
                    format="HH:mm"
                    value={block.endTime || null}
                    onChange={val => handleChange(index, 'endTime', val)}
                  />
                </Form.Item>

                <Form.Item
                  label="Schedule variance"
                  required
                  validateStatus={block.error ? 'error' : ''}
                  help={block.error}>
                  <TimePicker
                    disabled={block.locked}
                    format="HH:mm"
                    value={block.scheduleVariance}
                    onChange={val => handleChange(index, 'scheduleVariance', val)}
                  />
                </Form.Item>
              </Space>
            </Form>

            {isBlockComplete(block) && index === targets.length - 1 && (
              <Button type="dashed" onClick={addTarget}>
                Insert new target field
              </Button>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default RuleTargetScheduler;
